import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchQuizzes,
  fetchQuiz,
  createQuiz,
  updateQuiz,
  deleteQuiz,
  publishQuiz,
  archiveQuiz,
} from '@/services/quizzes';
import type { CreateQuizInput, UpdateQuizInput } from '@/types/quiz';

const QUIZZES_KEY = ['admin', 'quizzes'];

export function useQuizzes() {
  return useQuery({
    queryKey: QUIZZES_KEY,
    queryFn: fetchQuizzes,
  });
}

export function useQuiz(quizId: string | undefined) {
  return useQuery({
    queryKey: [...QUIZZES_KEY, quizId],
    queryFn: () => fetchQuiz(quizId!),
    enabled: !!quizId,
  });
}

export function useCreateQuiz() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQuizInput) => createQuiz(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUIZZES_KEY });
    },
  });
}

export function useUpdateQuiz() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quizId, data }: { quizId: string; data: UpdateQuizInput }) =>
      updateQuiz(quizId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUIZZES_KEY });
    },
  });
}

export function useDeleteQuiz() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quizId: string) => deleteQuiz(quizId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUIZZES_KEY });
    },
  });
}

export function usePublishQuiz() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quizId: string) => publishQuiz(quizId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUIZZES_KEY });
    },
  });
}

export function useArchiveQuiz() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quizId: string) => archiveQuiz(quizId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUIZZES_KEY });
    },
  });
}
